import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shuffle, Play, Sparkles, Loader2, GitFork, Flame, Share2, Crown, Download, Copy, Trophy } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import MashupChallenge from "@/components/MashupChallenge";
import RemixTree from "@/components/RemixTree";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const asset = (u) => (!u ? null : u.startsWith("http") ? u : `${BACKEND}${u}`);

function MashupCard({ g, ns = "mashup", onPlay, onRemix, onTree, onShare }) {
  return (
    <div data-testid={`${ns}-card-${g.id}`} className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden card-hover flex flex-col">
      <div className="aspect-video bg-[hsl(var(--secondary))] grid place-items-center relative overflow-hidden">
        {g.cover_image ? <img src={asset(g.cover_image)} alt={g.title} className="w-full h-full object-cover" loading="lazy" /> : <span className="text-5xl">{g.cover_emoji}</span>}
        <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-black/50 text-white">{g.dimension === "3d" ? "🧊 3D" : "🕹️ 2D"}</span>
        {g.champion && (
          <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-400 text-amber-950 flex items-center gap-1" data-testid={`${ns}-champion-${g.id}`}>
            <Crown className="w-3 h-3" /> {g.week_id ? g.week_id.replace("-W", " · wk ") : "Champion"}
          </span>
        )}
        {g.remix_count > 0 && (
          <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-fuchsia-500/90 text-white flex items-center gap-1" data-testid={`${ns}-remixcount-${g.id}`} title={`${g.remix_count} remix${g.remix_count === 1 ? "" : "es"}`}>
            <GitFork className="w-3 h-3" /> {g.remix_count}
          </span>
        )}
      </div>
      <div className="p-3 flex-1 flex flex-col">
        <div className="font-bold text-sm truncate">{g.title}</div>
        <div className="text-[11px] text-[hsl(var(--muted-foreground))] mb-1">by {g.owner_name} · ▶ {g.plays} · ♥ {g.likes}</div>
        {g.remixed_from && (
          <div className="text-[10px] text-fuchsia-600 dark:text-fuchsia-300 mb-1.5 truncate flex items-center gap-1" data-testid={`${ns}-remixfrom-${g.id}`} title={`Remixed from ${g.remixed_from.title} by ${g.remixed_from.owner_name}`}>
            <Shuffle className="w-3 h-3 shrink-0" /> Remixed from <b className="truncate">{g.remixed_from.title}</b> by {g.remixed_from.owner_name}
          </div>
        )}
        <div className="flex flex-wrap gap-1 mb-2">
          {(g.recipe || []).slice(0, 3).map((c, i) => (
            <span key={i} className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-fuchsia-500/10 text-fuchsia-600 dark:text-fuchsia-300 truncate max-w-[110px]">{c}</span>
          ))}
        </div>
        <div className="mt-auto space-y-1.5">
          <div className="flex gap-1.5">
            <Button size="sm" variant="ghost" className="rounded-full flex-1 h-8 px-2 text-xs" onClick={() => onTree(g)} data-testid={`${ns}-tree-${g.id}`}><GitFork className="w-3.5 h-3.5" /> Tree</Button>
            <Button size="sm" variant="ghost" className="rounded-full flex-1 h-8 px-2 text-xs" onClick={() => onShare(g)} data-testid={`${ns}-share-${g.id}`}><Share2 className="w-3.5 h-3.5" /> Share</Button>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="rounded-full flex-1" onClick={() => onPlay(g)} data-testid={`${ns}-play-${g.id}`}><Play className="w-3.5 h-3.5" /> Play</Button>
            <Button size="sm" className="rounded-full flex-1 bg-gradient-to-r from-fuchsia-500 to-cyan-500 text-white" onClick={() => onRemix(g)} data-testid={`${ns}-remix-${g.id}`}><Shuffle className="w-3.5 h-3.5" /> Remix</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MashupGallery() {
  const [rows, setRows] = useState(null);
  const [top, setTop] = useState([]);
  const [hall, setHall] = useState([]);
  const [treeId, setTreeId] = useState(null);
  const [share, setShare] = useState({ open: false });
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/mashup/gallery").then((r) => setRows(r.data || [])).catch(() => setRows([]));
    api.get("/mashup/challenge/top").then((r) => setTop(r.data || [])).catch(() => {});
    api.get("/mashup/hall").then((r) => setHall(r.data || [])).catch(() => {});
  }, []);

  const play = (g) => navigate(`/app/arcade/${g.id}`);
  const remix = (g) => { const qs = new URLSearchParams(g.remix || {}).toString(); navigate(`/app/mashup?${qs}`); };
  const openTree = (g) => setTreeId(g.id);

  const doShare = async (g) => {
    setShare({ open: true, loading: true, id: g.id, title: g.title });
    try {
      const { data } = await api.get(`/mashup/${g.id}/share-card`);
      setShare({ open: true, loading: false, id: g.id, title: g.title, url: asset(data.url), fallback: data.fallback, owner: data.owner });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't build a share card yet.");
      setShare({ open: false });
    }
  };
  const genCover = async () => {
    setShare((s) => ({ ...s, coverBusy: true }));
    try {
      await api.post(`/mashup/${share.id}/cover`);
      const { data } = await api.get(`/mashup/${share.id}/share-card`);
      toast.success("Cover art painted! ✨");
      setShare((s) => ({ ...s, coverBusy: false, url: `${asset(data.url)}?t=${Date.now()}`, fallback: data.fallback }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't generate cover art.");
      setShare((s) => ({ ...s, coverBusy: false }));
    }
  };
  const copyLink = () => {
    const link = `${window.location.origin}/app/arcade/${share.id}`;
    navigator.clipboard.writeText(link).then(() => toast.success("Play link copied!")).catch(() => toast.error("Couldn't copy."));
  };
  const socialShare = async () => {
    const link = `${window.location.origin}/app/arcade/${share.id}`;
    const payload = { title: share.title || "My GameView mashup", text: `Play my mashup "${share.title}" on GameView Studio!`, url: link };
    try {
      if (navigator.canShare && share.url) {
        try {
          const blob = await (await fetch(share.url)).blob();
          const file = new File([blob], `mashup-${share.id}.png`, { type: "image/png" });
          if (navigator.canShare({ files: [file] })) { await navigator.share({ ...payload, files: [file] }); return; }
        } catch { /* fall through */ }
      }
      if (navigator.share) { await navigator.share(payload); return; }
      await navigator.clipboard.writeText(link); toast.success("Play link copied!");
    } catch (err) {
      if (err && err.name === "AbortError") return; // user dismissed the share sheet
      try { await navigator.clipboard.writeText(link); toast.success("Play link copied!"); }
      catch { toast.error("Couldn't share — copy the link from your browser."); }
    }
  };

  const cardProps = { onPlay: play, onRemix: remix, onTree: openTree, onShare: doShare };

  return (
    <div className="max-w-6xl mx-auto p-5 lg:p-8" data-testid="mashup-gallery">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-11 h-11 rounded-2xl grid place-items-center bg-gradient-to-br from-fuchsia-500 to-cyan-500 shadow-lg"><Shuffle className="w-6 h-6 text-white" /></div>
        <h1 className="font-display text-3xl font-extrabold">Mashup Wall</h1>
      </div>
      <p className="text-sm text-[hsl(var(--muted-foreground))] mb-5">The coolest published mashups from the community. Play them, remix them, trace their family tree, or share a card.</p>

      <MashupChallenge />

      <div className="mb-6">
        <Button onClick={() => navigate("/app/mashup")} className="rounded-full bg-gradient-to-r from-fuchsia-500 to-cyan-500 text-white" data-testid="mashup-gallery-create">
          <Sparkles className="w-4 h-4" /> Make a mashup
        </Button>
      </div>

      {hall.length > 0 && (
        <div className="mb-8" data-testid="mashup-hall">
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="w-5 h-5 text-amber-500" />
            <h2 className="font-display text-lg font-bold">Hall of Fame</h2>
            <span className="text-xs text-[hsl(var(--muted-foreground))]">past weekly challenge champions</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {hall.map((g) => <MashupCard key={g.id} g={g} ns="hall" {...cardProps} />)}
          </div>
        </div>
      )}

      {top.length > 0 && (
        <div className="mb-8" data-testid="mashup-top-week">
          <div className="flex items-center gap-2 mb-3">
            <Flame className="w-5 h-5 text-amber-500" />
            <h2 className="font-display text-lg font-bold">Top this week</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {top.map((g) => <MashupCard key={g.id} g={g} ns="top" {...cardProps} />)}
          </div>
        </div>
      )}

      {rows === null ? (
        <div className="grid place-items-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[hsl(var(--primary))]" /></div>
      ) : rows.length === 0 ? (
        <div className="text-center py-20" data-testid="mashup-gallery-empty">
          <div className="text-5xl mb-3">🎛️</div>
          <div className="font-display text-xl font-bold">No mashups on the wall yet</div>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Be the first — build a mashup in the studio and publish it!</p>
        </div>
      ) : (
        <>
          {(top.length > 0 || hall.length > 0) && <h2 className="font-display text-lg font-bold mb-3">All mashups</h2>}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {rows.map((g) => <MashupCard key={g.id} g={g} {...cardProps} />)}
          </div>
        </>
      )}

      <RemixTree gameId={treeId} open={!!treeId} onOpenChange={(v) => { if (!v) setTreeId(null); }} />

      <Dialog open={share.open} onOpenChange={(v) => { if (!v) setShare({ open: false }); }}>
        <DialogContent data-testid="mashup-share-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Share2 className="w-5 h-5 text-fuchsia-500" /> Share your mashup</DialogTitle>
            <DialogDescription>A ready-to-post card{share.title ? ` for "${share.title}"` : ""}. Download it or copy the play link.</DialogDescription>
          </DialogHeader>
          {share.loading ? (
            <div className="grid place-items-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[hsl(var(--primary))]" /><span className="text-sm text-[hsl(var(--muted-foreground))] mt-2">Painting your card…</span></div>
          ) : share.url ? (
            <div className="space-y-2">
              <img src={share.url} alt="Mashup share card" className="w-full rounded-xl border border-[hsl(var(--border))]" data-testid="mashup-share-image" />
              {share.fallback && share.owner && (
                <Button variant="outline" className="rounded-full w-full border-fuchsia-500/50 text-fuchsia-600 dark:text-fuchsia-300" disabled={share.coverBusy} onClick={genCover} data-testid="mashup-gen-cover">
                  {share.coverBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Painting cover art…</> : <><Sparkles className="w-4 h-4" /> Generate real cover art (5 credits)</>}
                </Button>
              )}
              <div className="flex gap-2">
                <a href={share.url} download={`mashup-${share.id}.png`} className="flex-1" data-testid="mashup-share-download">
                  <Button variant="outline" className="rounded-full w-full"><Download className="w-4 h-4" /> Download</Button>
                </a>
                <Button className="rounded-full flex-1 bg-gradient-to-r from-fuchsia-500 to-cyan-500 text-white" onClick={socialShare} data-testid="mashup-share-native"><Share2 className="w-4 h-4" /> Share to socials</Button>
              </div>
              <Button variant="ghost" className="rounded-full w-full" onClick={copyLink} data-testid="mashup-share-copy"><Copy className="w-4 h-4" /> Copy play link</Button>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
